import { SIMPLE_TAG_SAMPLES, THEME_TAGS } from '@/lib/themeTags';
import { parseBlockSchema } from '@/lib/themeBlocks';

// ============================================================
// Rendu d'aperçu pour les thèmes.
// Transforme les template tags en contenus d'exemple afin de
// produire un aperçu fidèle et lisible (avant/après, test à blanc).
// ============================================================

const TAG_REGEX = /\{\{\s*([\w-]+)((?:\s+\w+="[^"]*")*)\s*\}\}/g;

function parseAttrs(raw: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /(\w+)="([^"]*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) {
    attrs[m[1]] = m[2];
  }
  return attrs;
}

/** Petit rendu d'exemple pour une balise de bloc, indépendant du CSS du thème. */
function blockPlaceholder(name: string, attrs: Record<string, string>): string {
  const limit = parseInt(attrs.limit || '3', 10) || 3;
  const count = Math.min(Math.max(limit, 2), 8);
  const cards = Array.from({ length: count }).map((_, i) => `
    <div style="border:1px solid rgba(0,0,0,.08);border-radius:12px;overflow:hidden;background:#fff;">
      <div style="height:130px;background:linear-gradient(135deg,#eee,#ddd);display:flex;align-items:center;justify-content:center;color:#999;font-size:22px;">${i % 2 ? '🛍️' : '🔧'}</div>
      <div style="padding:12px;">
        <div style="height:10px;width:70%;background:#e5e5e5;border-radius:4px;margin-bottom:8px;"></div>
        <div style="height:10px;width:40%;background:#eee;border-radius:4px;"></div>
      </div>
    </div>`).join('');

  const wrap = (inner: string) => `<div style="margin:8px 0;">${inner}</div>`;
  const grid = (cols: number) => `<div style="display:grid;grid-template-columns:repeat(${cols},1fr);gap:16px;">${cards}</div>`;

  switch (name) {
    case 'hero':
      return wrap(`<div style="position:relative;border-radius:16px;overflow:hidden;height:260px;background:linear-gradient(135deg,#c9b8a8,#8a7a6a);display:flex;align-items:center;justify-content:center;flex-direction:column;gap:12px;color:#fff;"><div style="height:16px;width:260px;background:rgba(255,255,255,.85);border-radius:6px;"></div><div style="height:10px;width:180px;background:rgba(255,255,255,.5);border-radius:6px;"></div><div style="height:40px;width:150px;background:rgba(255,255,255,.9);border-radius:9999px;margin-top:6px;"></div></div>`);
    case 'heading':
      return wrap(`<div style="height:24px;width:${Math.min(60, Math.max(20, String(attrs.text || '').length * 2))}%;background:#ddd;border-radius:6px;margin:14px 0;"></div>`);
    case 'text':
      return wrap(`<div>${Array.from({ length: 3 }).map(() => '<div style="height:10px;background:#eee;border-radius:4px;margin-bottom:8px;"></div>').join('')}<div style="height:10px;width:60%;background:#eee;border-radius:4px;"></div></div>`);
    case 'image':
      return wrap(`<div style="height:200px;background:linear-gradient(135deg,#eee,#ddd);border-radius:16px;display:flex;align-items:center;justify-content:center;color:#aaa;font-size:26px;">🖼️</div>`);
    case 'tabs':
      return wrap(`<div><div style="display:flex;gap:8px;margin-bottom:14px;">${Array.from({ length: 3 }).map((_x, i) => `<div style="height:34px;width:110px;background:${i === 0 ? '#333' : '#eee'};border-radius:9999px;"></div>`).join('')}</div><div style="height:10px;background:#eee;border-radius:4px;margin-bottom:8px;"></div><div style="height:10px;width:70%;background:#eee;border-radius:4px;"></div></div>`);
    case 'before_after':
      return wrap(`<div style="position:relative;height:200px;border-radius:16px;overflow:hidden;background:linear-gradient(90deg,#8a7a6a 0%,#8a7a6a 50%,#c9b8a8 50%,#c9b8a8 100%);display:flex;align-items:center;justify-content:center;color:#fff;font:600 13px sans-serif;">↔︎ Avant / Après</div>`);
    case 'trust_banner':
      return wrap(`<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;">${Array.from({ length: 4 }).map(() => '<div style="display:flex;align-items:center;gap:10px;border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:14px;background:#fff;"><div style="width:32px;height:32px;border-radius:50%;background:#eee;"></div><div style="height:10px;flex:1;background:#eee;border-radius:4px;"></div></div>').join('')}</div>`);
    case 'cta':
      return wrap(`<div style="border-radius:16px;background:#333;color:#fff;padding:32px;text-align:center;"><div style="height:14px;width:220px;background:rgba(255,255,255,.8);border-radius:6px;margin:0 auto 12px;"></div><div style="height:38px;width:140px;background:rgba(255,255,255,.9);border-radius:9999px;margin:0 auto;"></div></div>`);
    case 'spacer':
      return `<div style="height:${parseInt(attrs.height || '48', 10) || 48}px;"></div>`;
    case 'divider':
      return wrap('<hr style="border:none;border-top:1px solid rgba(0,0,0,.1);">');
    case 'products':
    case 'featured_products':
    case 'marketplace_products':
      return wrap(grid(3));
    case 'services':
      return wrap(grid(3));
    case 'blog_posts':
      return wrap(grid(3));
    case 'portfolio':
    case 'gallery':
      return wrap(grid(4));
    case 'hero_banners':
      return wrap(`<div style="position:relative;border-radius:16px;overflow:hidden;height:260px;background:linear-gradient(135deg,#c9b8a8,#8a7a6a);display:flex;align-items:flex-end;padding:28px;">
        <div style="color:#fff;"><div style="height:14px;width:200px;background:rgba(255,255,255,.85);border-radius:6px;margin-bottom:10px;"></div><div style="height:10px;width:120px;background:rgba(255,255,255,.55);border-radius:6px;"></div></div>
        <div style="position:absolute;bottom:16px;right:20px;display:flex;gap:6px;">${Array.from({ length: 3 }).map((_, i) => `<span style="width:${i === 0 ? 20 : 8}px;height:8px;border-radius:9999px;background:${i === 0 ? '#fff' : 'rgba(255,255,255,.5)'};display:inline-block;"></span>`).join('')}</div>
      </div>`);
    case 'hebergements':
      return wrap(grid(3));
    case 'partners':
      return wrap(`<div style="display:flex;flex-wrap:wrap;gap:16px;">${Array.from({ length: 5 }).map(() => '<div style="width:120px;height:48px;background:#eee;border-radius:8px;"></div>').join('')}</div>`);
    case 'team':
      return wrap(`<div style="display:flex;flex-wrap:wrap;gap:20px;">${Array.from({ length: 4 }).map(() => '<div style="text-align:center;width:120px;"><div style="width:80px;height:80px;border-radius:50%;background:#e5e5e5;margin:0 auto 8px;"></div><div style="height:9px;width:70%;background:#eee;border-radius:4px;margin:0 auto;"></div></div>').join('')}</div>`);
    case 'testimonials':
      return wrap(`<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;">${Array.from({ length: 3 }).map(() => '<div style="border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:16px;background:#fff;"><div style="height:10px;background:#eee;border-radius:4px;margin-bottom:8px;"></div><div style="height:10px;width:80%;background:#eee;border-radius:4px;margin-bottom:12px;"></div><div style="height:9px;width:40%;background:#ddd;border-radius:4px;"></div></div>').join('')}</div>`);
    case 'stats':
      return wrap(`<div style="display:grid;grid-template-columns:repeat(4,1fr);gap:16px;text-align:center;">${Array.from({ length: 4 }).map(() => '<div><div style="font-size:1.8rem;font-weight:700;color:#333;">128</div><div style="height:9px;width:60%;background:#eee;border-radius:4px;margin:6px auto 0;"></div></div>').join('')}</div>`);
    case 'faq':
      return wrap(`<div>${Array.from({ length: 4 }).map(() => '<div style="border-bottom:1px solid rgba(0,0,0,.08);padding:14px 4px;"><div style="height:10px;width:60%;background:#e5e5e5;border-radius:4px;"></div></div>').join('')}</div>`);
    case 'contact_form':
    case 'booking_form':
      return wrap(`<div style="border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:20px;background:#fff;max-width:520px;">
        ${Array.from({ length: name === 'booking_form' ? 4 : 3 }).map(() => '<div style="margin-bottom:12px;"><div style="height:8px;width:30%;background:#eee;border-radius:4px;margin-bottom:6px;"></div><div style="height:38px;border:1px solid rgba(0,0,0,.1);border-radius:8px;background:#fafafa;"></div></div>').join('')}
        <div style="height:42px;background:#333;border-radius:9999px;"></div>
      </div>`);
    case 'marketplace_search':
      return wrap(`<div style="height:46px;border:1px solid rgba(0,0,0,.12);border-radius:9999px;background:#fff;display:flex;align-items:center;padding:0 18px;color:#999;">🔍&nbsp;${attrs.placeholder || 'Rechercher...'}</div>`);
    case 'marketplace_categories':
      return wrap(`<div style="display:flex;flex-wrap:wrap;gap:10px;">${Array.from({ length: 6 }).map(() => '<div style="height:34px;width:110px;background:#eee;border-radius:9999px;"></div>').join('')}</div>`);
    case 'marketplace_sellers':
      return wrap(`<div style="display:grid;grid-template-columns:repeat(3,1fr);gap:16px;">${Array.from({ length: 3 }).map(() => '<div style="border:1px solid rgba(0,0,0,.08);border-radius:12px;padding:16px;background:#fff;"><div style="width:44px;height:44px;border-radius:50%;background:#e5e5e5;margin-bottom:10px;"></div><div style="height:10px;width:60%;background:#eee;border-radius:4px;"></div></div>').join('')}</div>`);
    default:
      return wrap(`<div style="border:1px dashed rgba(0,0,0,.2);border-radius:12px;padding:20px;text-align:center;color:#888;font:13px/1.5 sans-serif;">Bloc dynamique <strong>{{${name}}}</strong></div>`);
  }
}

/** Rend un schéma de blocs (page « React ») en HTML d'aperçu fidèle. */
export function renderBlockSchemaPreview(content: string): string | null {
  const schema = parseBlockSchema(content);
  if (!schema) return null;
  return schema.blocks.map((b) => {
    const attrs: Record<string, string> = {};
    Object.entries(b.props || {}).forEach(([k, v]) => { attrs[k] = String(v ?? ''); });
    return blockPlaceholder(b.type, attrs);
  }).join('\n');
}

/** Remplace les template tags par un rendu d'exemple pour un aperçu fidèle. */
export function renderPreviewContent(content: string): string {
  const blockHtml = renderBlockSchemaPreview(content);
  if (blockHtml !== null) return blockHtml;
  return (content || '').replace(TAG_REGEX, (_full, name: string, rawAttrs: string) => {
    const known = THEME_TAGS.some((t) => t.name === name);
    if (!known) return `<span style="background:#fee;color:#c00;padding:1px 5px;border-radius:4px;font:12px monospace;">{{${name}}}</span>`;
    if (SIMPLE_TAG_SAMPLES[name] !== undefined) return SIMPLE_TAG_SAMPLES[name];
    return blockPlaceholder(name, parseAttrs(rawAttrs || ''));
  });
}

export interface PreviewDocumentInput {
  stylesheet: string;
  pages: Record<string, string>;
  homeKey?: string;
  title?: string;
}

/**
 * Assemble un document HTML complet (head + header + page + footer)
 * prêt à être injecté dans une iframe d'aperçu.
 */
export function buildThemePreviewDocument({
  stylesheet,
  pages,
  homeKey = 'home',
  title = 'Aperçu du thème',
}: PreviewDocumentInput): string {
  const render = (c?: string) => renderPreviewContent(c || '');
  const headTags = render(pages.head);
  const headerHtml = render(pages.header);
  const footerHtml = render(pages.footer);
  const mainKey = pages[homeKey] !== undefined ? homeKey : (pages.home !== undefined ? 'home' : Object.keys(pages)[0]);
  const mainHtml = render(mainKey ? pages[mainKey] : '');

  const body = `${headerHtml}<main>${mainHtml || '<p style="padding:40px;font:14px sans-serif;color:#999;">Cette page est vide</p>'}</main>${footerHtml}`;

  return `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>${stylesheet}</style>
  ${headTags}
</head>
<body>
  ${body}
</body>
</html>`;
}